import React from "react";
import { AboutSection } from "../AboutSection";
import { QuickFacts } from "../QuickFacts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/components/auth/AuthProvider";
import { useIsMobile } from "@/hooks/use-mobile";
import { FAQSection } from "../FAQSection";
import { FacilitiesSection } from "../FacilitiesSection";
import { AmenitiesSection } from "../AmenitiesSection";
import { InspectionBadge } from "../badges/InspectionBadge";
import { FairDealBadge } from "../badges/FairDealBadge";
import { PriceDisplay } from "../badges/PriceDisplay";
import { UpcomingEvents } from "../public/UpcomingEvents";
import { StaffProfiles } from "../public/StaffProfiles";
import { ContactForm } from "../public/ContactForm";
import { ReviewSection } from "../public/ReviewSection";

interface OverviewTabProps {
  nursingHome: {
    center_name: string;
    nh_slug: string;
    county_slug?: string;
    description?: string;
    person_in_charge?: string;
    manager_name?: string;
    standardized_address?: string;
    standardized_phone_number?: string;
    max_residents?: number;
    max_occupancy?: number;
    category?: string;
    year_established?: number;
    hiqa_profile_url?: string;
    email?: string;
    official_website?: string;
    facilities?: string[];
    amenities?: string[];
    inspection_rating?: string;
    fair_deal_approved?: boolean;
    last_inspection_date?: string;
    price_range?: string;
    available_beds?: number | null;
    accepting_enquiries?: boolean;
    virtual_tour_url?: string;
    brochure_url?: string;
  };
  showShortDescription?: boolean;
  countyName?: string;
}

export function OverviewTab({ 
  nursingHome, 
  showShortDescription = true,
  countyName 
}: OverviewTabProps) {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  
  // Use max_residents or max_occupancy, whichever is available
  const maxResidents = nursingHome.max_residents || nursingHome.max_occupancy;
  
  return (
    <div className="space-y-6">
      {/* Key Info Badges */}
      {(nursingHome.inspection_rating || nursingHome.fair_deal_approved || nursingHome.price_range) && (
        <Card className="overflow-hidden">
          <CardHeader className="pb-2 bg-grandpal-beige/20">
            <CardTitle className="text-lg text-grandpal-green-dark">Key Information</CardTitle>
          </CardHeader>
          <CardContent className="p-5 lg:p-6">
            <div className="flex flex-wrap gap-3 items-center">
              <InspectionBadge 
                rating={nursingHome.inspection_rating} 
                lastInspectionDate={nursingHome.last_inspection_date} 
              />
              <FairDealBadge approved={nursingHome.fair_deal_approved} />
            </div>
            {nursingHome.price_range && (
              <div className="mt-3 pt-3 border-t">
                <PriceDisplay priceRange={nursingHome.price_range} />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Quick Facts Section */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-2 bg-grandpal-beige/20">
          <CardTitle className="text-lg text-grandpal-green-dark">Quick Facts</CardTitle>
        </CardHeader>
        <CardContent className="p-5 lg:p-6">
          <QuickFacts 
            address={nursingHome.standardized_address}
            phone={nursingHome.standardized_phone_number}
            email={nursingHome.email}
            website={nursingHome.official_website}
            manager={nursingHome.manager_name}
            personInCharge={nursingHome.person_in_charge}
            maxResidents={maxResidents}
            yearEstablished={nursingHome.year_established}
            visitingHours="10:00 AM - 8:00 PM"
            availableBeds={nursingHome.available_beds}
            acceptingEnquiries={nursingHome.accepting_enquiries}
            virtualTourUrl={nursingHome.virtual_tour_url}
            brochureUrl={nursingHome.brochure_url}
          />
        </CardContent>
      </Card>
      
      <AboutSection
        name={nursingHome.center_name}
        description={nursingHome.description || `Information about ${nursingHome.center_name} will be available soon.`}
        visitingHours="10:00 AM - 8:00 PM" // This could be dynamic if available from API
        category={nursingHome.category}
        maxOccupancy={maxResidents}
      />
      {/* Add Facilities and Amenities sections if data is present */}
      {nursingHome.facilities && nursingHome.facilities.length > 0 && (
        <FacilitiesSection facilities={nursingHome.facilities} />
      )}
      {nursingHome.amenities && nursingHome.amenities.length > 0 && (
        <AmenitiesSection amenities={nursingHome.amenities} />
      )}
      {/* Upcoming Events */}
      <UpcomingEvents nhSlug={nursingHome.nh_slug} />
      
      {/* Staff Profiles */}
      <StaffProfiles nhSlug={nursingHome.nh_slug} />

      {/* Reviews */}
      <ReviewSection nhSlug={nursingHome.nh_slug} nursingHomeName={nursingHome.center_name || ""} />

      {/* Contact Form */}
      <ContactForm nhSlug={nursingHome.nh_slug} nursingHomeName={nursingHome.center_name || ""} countySlug={nursingHome.county_slug || ""} />

      <FAQSection nursingHomeName={nursingHome.center_name} />
    </div>
  );
}
